import { useRef, useState, useEffect } from "react";
import { Camera, RefreshCw, Image as ImageIcon, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";

// Captures a photo via camera (getUserMedia) with optional gallery fallback.
// Returns a JPEG data URL through onCapture. Shared by Temperaturas (defaults)
// and Tareas (compact + confirmStep + planilla texts).
export function PhotoCapture({
  onCapture,
  testidPrefix = "photo",
  instruction = "Toma una fotografía del termómetro",
  ctaLabel = "Cámara",
  allowGallery = true,
  compact = false,
  confirmStep = false,
}) {
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [captured, setCaptured] = useState(null);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStreaming(false);
  };

  // Attach the stream only AFTER the <video> element is in the DOM (fixes black screen).
  useEffect(() => {
    if (streaming && streamRef.current && videoRef.current) {
      const v = videoRef.current;
      v.srcObject = streamRef.current;
      v.setAttribute("playsinline", "true");
      v.muted = true;
      v.play().catch(() => {});
    }
  }, [streaming]);

  useEffect(() => () => stopCamera(), []);

  const mapError = (e) => {
    const name = e?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError")
      return "Permiso de cámara denegado. Habilítalo en los ajustes del navegador.";
    if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError")
      return "No se detectó una cámara en este dispositivo.";
    if (name === "NotReadableError" || name === "TrackStartError")
      return "La cámara está en uso por otra aplicación.";
    return "No se pudo abrir la cámara. Inténtalo de nuevo.";
  };

  const startCamera = async () => {
    setError(null);
    setCaptured(null);
    if (!window.isSecureContext) {
      setError("La cámara requiere una conexión segura (HTTPS).");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Tu navegador no permite usar la cámara.");
      return;
    }
    setStarting(true);
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } }, audio: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      streamRef.current = stream;
      setStreaming(true);
    } catch (e) {
      setError(mapError(e));
    } finally {
      setStarting(false);
    }
  };

  const toJpeg = (source, w, h) => {
    const canvas = document.createElement("canvas");
    const scale = Math.min(1, 1600 / Math.max(w, h));
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.82);
  };

  const emit = (dataUrl) => {
    if (confirmStep) setCaptured(dataUrl);
    else onCapture(dataUrl);
  };

  const shoot = () => {
    const v = videoRef.current;
    if (!v) return;
    const dataUrl = toJpeg(v, v.videoWidth || 720, v.videoHeight || 960);
    stopCamera();
    emit(dataUrl);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => emit(toJpeg(img, img.width, img.height));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const useFoto = () => { const c = captured; setCaptured(null); onCapture(c); };
  const repeat = () => { setCaptured(null); startCamera(); };

  // ---- confirm preview (Usar / Repetir) ----
  if (confirmStep && captured) {
    return (
      <div className="space-y-3">
        <button type="button" onClick={() => window.open(captured, "_blank")} className="block w-full">
          <img data-testid={`${testidPrefix}-preview`} src={captured} alt="Evidencia" className="mx-auto max-h-56 w-auto rounded-xl object-contain" />
        </button>
        <div className="grid grid-cols-2 gap-3">
          <Button data-testid={`${testidPrefix}-use-btn`} onClick={useFoto} className="h-11 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]"><Check className="mr-2 h-4 w-4" /> Usar foto</Button>
          <Button data-testid={`${testidPrefix}-retake-btn`} variant="outline" onClick={repeat} className="h-11 rounded-xl"><RefreshCw className="mr-2 h-4 w-4" /> Repetir foto</Button>
        </div>
      </div>
    );
  }

  // ---- live camera ----
  if (streaming) {
    return (
      <div className="space-y-3">
        <div className={`relative w-full overflow-hidden rounded-2xl bg-slate-900 ${compact ? "max-h-[55vh]" : "aspect-[3/4]"}`}>
          <video ref={videoRef} className={`w-full ${compact ? "max-h-[55vh] object-contain" : "h-full object-cover"}`} playsInline muted autoPlay />
        </div>
        {compact ? (
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid={`${testidPrefix}-shoot-btn`} onClick={shoot} className="h-12 rounded-xl bg-[#2563EB] text-base hover:bg-[#1D4ED8]"><Camera className="mr-2 h-5 w-5" /> Capturar</Button>
            <Button data-testid={`${testidPrefix}-cancel-btn`} variant="outline" onClick={stopCamera} className="h-12 rounded-xl"><X className="mr-2 h-4 w-4" /> Cancelar</Button>
          </div>
        ) : (
          <Button data-testid={`${testidPrefix}-shoot-btn`} onClick={shoot} className="h-14 w-full rounded-xl text-base"><Camera className="mr-2 h-5 w-5" /> Capturar</Button>
        )}
      </div>
    );
  }

  // ---- idle ----
  if (compact) {
    return (
      <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-[#2563EB]"><Camera className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[#153D35]">Evidencia fotográfica</p>
            <p className="text-xs text-[#68756F]">{instruction}</p>
          </div>
        </div>
        {error && <p data-testid={`${testidPrefix}-error`} className="mt-2 text-xs text-red-600">{error}{!allowGallery ? " Debes usar la cámara para esta tarea." : ""}</p>}
        <div className="mt-3 space-y-2">
          <Button data-testid={`${testidPrefix}-start-camera-btn`} disabled={starting} onClick={startCamera} className="h-11 w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Camera className="mr-2 h-4 w-4" /> {starting ? "Abriendo…" : ctaLabel}
          </Button>
          {allowGallery && (
            <Button data-testid={`${testidPrefix}-upload-btn`} variant="ghost" onClick={() => fileRef.current?.click()} className="h-9 w-full rounded-xl text-sm text-[#68756F]">
              <ImageIcon className="mr-2 h-4 w-4" /> Seleccionar imagen desde dispositivo
            </Button>
          )}
        </div>
        <input ref={fileRef} data-testid={`${testidPrefix}-file-input`} type="file" accept="image/*" className="hidden" onChange={onFile} />
      </div>
    );
  }

  // ---- idle (default / Temperaturas) ----
  return (
    <div className="space-y-3">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-slate-900">
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-slate-300">
          <Camera className="h-10 w-10" />
          <p className="px-6 text-center text-sm">{error || instruction}</p>
        </div>
      </div>
      <div className={allowGallery ? "grid grid-cols-2 gap-3" : ""}>
        <Button data-testid={`${testidPrefix}-start-camera-btn`} disabled={starting} onClick={startCamera} className="h-14 w-full rounded-xl text-base">
          <Camera className="mr-2 h-5 w-5" /> {starting ? "Abriendo…" : ctaLabel}
        </Button>
        {allowGallery && (
          <Button data-testid={`${testidPrefix}-upload-btn`} variant="outline" onClick={() => fileRef.current?.click()} className="h-14 rounded-xl text-base">
            <ImageIcon className="mr-2 h-5 w-5" /> Subir imagen
          </Button>
        )}
      </div>
      <input ref={fileRef} data-testid={`${testidPrefix}-file-input`} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
    </div>
  );
}
